/**
 * Model Support Grid Section
 * Display supported models with auto-rotating carousel
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { staggerContainerVariants, itemVariants } from '@/components/animations/variants';
import { CompanyLogo } from '@/components/logos/logo-component';

type Provider = 'openai' | 'anthropic' | 'google';

interface ProviderData {
  provider: Provider;
  name: string;
  models: string[];
  color: string;
}

const providerData: ProviderData[] = [
  {
    provider: 'openai',
    name: 'OpenAI',
    models: ['GPT-5 Mini', 'GPT-4o', 'GPT-5'],
    color: 'from-emerald-500 to-teal-500',
  },
  {
    provider: 'anthropic',
    name: 'Anthropic',
    models: ['Claude Haiku 4.5', 'Claude Sonnet 4.5', 'Claude Opus 4.1'],
    color: 'from-orange-500 to-red-500',
  },
  {
    provider: 'google',
    name: 'Google',
    models: ['Gemini 2.5 Flash-Lite', 'Gemini 2.5 Flash', 'Gemini 2.5 Pro'],
    color: 'from-blue-500 to-purple-500',
  },
];

function ProviderCarousel({ data }: { data: ProviderData }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % data.models.length);
    }, 2000); // Change model every 2 seconds

    return () => clearInterval(interval);
  }, [data.models.length]);

  return (
    <motion.div
      variants={itemVariants}
      className="relative"
    >
      <div className="bg-card border border-border rounded-xl p-8 h-full flex flex-col items-center justify-center space-y-6 min-h-[280px]">
        {/* Provider Logo */}
        <div className="flex items-center justify-center p-4 rounded-xl bg-background/50 border border-border/50">
          <CompanyLogo provider={data.provider} size="lg" />
        </div>

        {/* Provider Name */}
        <h3 className="text-2xl font-bold">{data.name}</h3>

        {/* Rotating Model Names */}
        <div className="relative h-16 flex items-center justify-center w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="absolute"
            >
              <p className="text-lg font-medium text-center text-muted-foreground">
                {data.models[currentIndex]}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom Border Gradient */}
        <div className={`h-1 w-full rounded-full bg-gradient-to-r ${data.color}`} />
      </div>
    </motion.div>
  );
}

export function ModelGridSection() {
  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Support for Top AI Models
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Access the latest models from OpenAI, Anthropic, and Google—all in one place
          </p>
        </motion.div>

        {/* Provider Carousels */}
        <motion.div
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
        >
          {providerData.map((data) => (
            <ProviderCarousel key={data.provider} data={data} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
